import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../../core/services/modal.service';
import {
  SegmentService,
  DriverConflict,
  EquipmentConflict,
} from '../../../../core/services/segment.service';
import { UserService } from '../../../../core/services/user.service';
import { AssetService } from '../../../../core/services/asset.service';
import { YardService } from '../../../../core/services/yard.service';
import { GoogleMapsService } from '../../../../core/services/google-maps.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { SelectComponent, SelectOption } from '../../../../shared/ui/forms/select/select.component';
import { SelectChipComponent, ChipOption } from '../../../../shared/ui/forms/select-chip/select-chip.component';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import {
  ButtonGroupComponent,
  ButtonGroupOption,
} from '../../../../shared/ui/forms/button-group/button-group.component';
import { AddressFormComponent } from '../../../../shared/ui/forms/address-form/address-form.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { DriverCredentialWarningComponent } from '../../../../shared/ui/driver-credential-warning/driver-credential-warning.component';
import { Load } from '../../../../core/models/load.model';
import { ExchangeType, LoadSegment } from '../../../../core/models/load-segment.model';
import { Stop } from '../../../../core/models/stop.model';
import { Address } from '../../../../core/models/address.model';
import { Yard } from '../../../../core/models/yard.model';
import {
  BoundaryServicedBy,
  isRelayOnlyStopType,
} from '../../../../core/utils/itinerary-reconciler';
import { getLoadEndDate } from '../../../../core/utils/driver-credentials';

export type LegModalMode = 'split' | 'edit';

const PRESET_REASONS = [
  'Scheduled Relay',
  'HOS / Hours Limit',
  'Drop & Hook',
  'Vehicle Breakdown',
  'Driver Home Time',
] as const;

@Component({
  selector: 'app-leg-assignment-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    InputComponent,
    SelectComponent,
    SelectChipComponent,
    TextareaComponent,
    CheckboxComponent,
    ButtonGroupComponent,
    AddressFormComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
    DriverCredentialWarningComponent,
  ],
  templateUrl: './leg-assignment-modal.component.html',
})
export class LegAssignmentModalComponent implements OnInit, OnDestroy {
  readonly hasCustomActions = true;

  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly userService = inject(UserService);
  private readonly assetService = inject(AssetService);
  private readonly yardService = inject(YardService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly toastService = inject(ToastService);

  readonly isSaving = signal(false);
  readonly mode = signal<LegModalMode>('split');
  readonly splitPoint = signal<number>(-1); // > 0 = existing stopNumber, < 0 = insert relay after Math.abs(splitPoint)
  readonly exchangeType = signal<ExchangeType>('full_swap');
  readonly locationType = signal<'saved' | 'custom'>('saved');
  readonly selectedYardId = signal<string | null>(null);
  readonly isTeamLeg = signal(false);
  readonly servicedBy = signal<BoundaryServicedBy>('outgoing');
  readonly selectedReasonChip = signal<string>('Scheduled Relay');

  readonly selectedDriver1Id = signal<string | null>(null);
  readonly selectedDriver2Id = signal<string | null>(null);
  readonly driverConflicts = signal<DriverConflict[]>([]);
  readonly truckConflicts = signal<EquipmentConflict[]>([]);

  load!: Load;
  segments = signal<LoadSegment[]>([]);
  targetSegment = signal<LoadSegment | null>(null);
  form!: FormGroup;
  private subs = new Subscription();

  readonly reasonChipOptions: ChipOption[] = [
    ...PRESET_REASONS.map((r) => ({ value: r, label: r })),
    { value: 'Other', label: 'Other' },
  ];

  readonly exchangeTypeOptions: ButtonGroupOption[] = [
    { value: 'full_swap', label: 'Driver + Truck', icon: 'ArrowRightLeft' },
    { value: 'driver_only', label: 'Driver Only', icon: 'User' },
    { value: 'equipment_only', label: 'Equipment Only', icon: 'Truck' },
  ];

  readonly locationTypeOptions: ButtonGroupOption[] = [
    { value: 'saved', label: 'Saved Yard / Terminal', icon: 'Warehouse' },
    { value: 'custom', label: 'Custom Address', icon: 'MapPin' },
  ];

  readonly servicedByOptions: ButtonGroupOption[] = [
    { value: 'outgoing', label: 'Outgoing Leg Driver', icon: 'UserCheck' },
    { value: 'incoming', label: 'Oncoming Leg Driver', icon: 'UserPlus' },
  ];

  readonly hasSavedYards = computed(() => this.yardService.allYards().length > 0);

  readonly yardOptions = computed<SelectOption[]>(() =>
    this.yardService.allYards().map((yard) => ({
      value: yard.id,
      label: `${yard.name} (${yard.isTerminal ? 'Terminal' : 'Yard'})`,
      icon: yard.isTerminal ? 'Building' : 'Warehouse',
    }))
  );

  readonly selectedYard = computed<Yard | undefined>(() => {
    const id = this.selectedYardId();
    if (!id) return undefined;
    return this.yardService.allYards().find((y) => y.id === id);
  });

  readonly splitPointOptions = computed<SelectOption[]>(() => {
    const stops = this.load?.stops || [];
    const activeSegs = this.segments();
    const alreadySplitStops = new Set<number>();
    for (let i = 0; i < activeSegs.length - 1; i++) {
      alreadySplitStops.add(activeSegs[i].toStopNumber);
    }

    const opts: SelectOption[] = [];

    // 1. Mid-route Relay insertions between Stop i and Stop i+1
    for (let i = 0; i < stops.length - 1; i++) {
      const curr = stops[i];
      const next = stops[i + 1];
      const currNum = curr.stopNumber || i + 1;
      const nextNum = next.stopNumber || i + 2;
      const currLabel = curr.address?.city || curr.companyName || `Stop ${currNum}`;
      const nextLabel = next.address?.city || next.companyName || `Stop ${nextNum}`;
      opts.push({
        value: String(-currNum),
        label: `Insert Relay Yard between Stop ${currNum} (${currLabel}) & Stop ${nextNum} (${nextLabel})`,
        icon: 'Warehouse',
      });

      // 2. Existing intermediate stops (Stops 2..N-1) not already split
      if (i > 0 && !alreadySplitStops.has(currNum)) {
        const stopDesc = curr.companyName || currLabel;
        opts.push({
          value: String(currNum),
          label: `At Existing Stop ${currNum} — ${stopDesc}`,
          icon: 'MapPin',
        });
      }
    }

    // Check last intermediate stop (stops.length - 2) if missed by loop ordering
    if (stops.length > 2) {
      const lastInter = stops[stops.length - 2];
      const num = lastInter.stopNumber || stops.length - 1;
      if (!alreadySplitStops.has(num) && !opts.some((o) => o.value === String(num))) {
        const stopDesc =
          lastInter.companyName || lastInter.address?.city || `Stop ${num}`;
        opts.push({
          value: String(num),
          label: `At Existing Stop ${num} — ${stopDesc}`,
          icon: 'MapPin',
        });
      }
    }

    return opts;
  });

  readonly isInsertingNewRelayStop = computed(
    () => this.mode() === 'split' && this.splitPoint() < 0
  );

  readonly existingSplitStop = computed<Stop | null>(() => {
    if (this.mode() === 'split') {
      const pt = this.splitPoint();
      if (pt <= 0) return null;
      return (this.load?.stops || []).find((s) => s.stopNumber === pt) || null;
    }
    const target = this.targetSegment();
    if (!target || target.segmentNumber <= 1) return null;
    return (this.load?.stops || []).find((s) => s.stopNumber === target.fromStopNumber) || null;
  });

  readonly showCommercialBoundaryDuty = computed(() => {
    const stop = this.existingSplitStop();
    if (!stop) return false;
    return !isRelayOnlyStopType(stop.stopType);
  });

  /** Resolves the exact outgoing leg being split (works for 1st, 2nd, 3rd+ relays). */
  readonly outgoingSegment = computed<LoadSegment | null>(() => {
    const segs = this.segments();
    if (this.mode() === 'edit') {
      const target = this.targetSegment();
      if (!target || target.segmentNumber <= 1) return null;
      return segs.find((s) => s.segmentNumber === target.segmentNumber - 1) || null;
    }

    const pt = this.splitPoint();
    if (segs.length === 0) return null;
    if (pt > 0) {
      return (
        segs.find((s) => s.fromStopNumber < pt && s.toStopNumber > pt) ||
        segs[segs.length - 1]
      );
    }
    const afterNum = Math.abs(pt);
    return (
      segs.find((s) => s.fromStopNumber <= afterNum && s.toStopNumber >= afterNum + 1) ||
      segs[segs.length - 1]
    );
  });

  readonly outgoingSummary = computed(() => {
    const seg = this.outgoingSegment();
    const dId = seg?.driverId || this.load?.driver1Id;
    const dName = dId
      ? this.userService.getDriverDisplayName(dId, seg?.driverName || this.load?.driver1Name)
      : 'Unassigned';
    const trk =
      seg?.truckUnitNumber ||
      (seg?.truckId ? this.assetService.getAssetName(seg.truckId) : '') ||
      (this.load?.truckId ? this.assetService.getAssetName(this.load.truckId) : '') ||
      'No Truck';
    const trl =
      seg?.trailerUnitNumber ||
      (seg?.trailerId ? this.assetService.getAssetName(seg.trailerId) : '') ||
      (this.load?.trailerId ? this.assetService.getAssetName(this.load.trailerId) : '') ||
      'No Trailer';
    const legTitle = seg ? `Leg ${seg.segmentNumber}` : 'Leg 1';
    return { legTitle, driverName: dName, equipment: `${trk} / ${trl}` };
  });

  readonly driverOptions = computed<SelectOption[]>(() =>
    this.userService
      .activeDrivers()
      .map((driver) => ({
        label: this.userService.getDriverDropdownLabel(driver),
        value: driver.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly truckOptions = computed<SelectOption[]>(() =>
    this.assetService
      .trucks()
      .map((truck) => ({
        label: truck.unitNumber || truck.licensePlate || truck.id,
        value: truck.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly trailerOptions = computed<SelectOption[]>(() =>
    this.assetService
      .trailers()
      .map((trailer) => {
        let label = trailer.unitNumber || trailer.licensePlate || trailer.id;
        if (trailer.specs?.trailerType) {
          label += ` - ${trailer.specs.trailerType}`;
        }
        return { label, value: trailer.id };
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  readonly loadEndDate = computed(() => getLoadEndDate(this.load?.stops || []));
  readonly selectedDriver1 = computed(
    () => this.userService.activeDrivers().find((d) => d.id === this.selectedDriver1Id()) || null
  );
  readonly selectedDriver2 = computed(
    () => this.userService.activeDrivers().find((d) => d.id === this.selectedDriver2Id()) || null
  );

  ngOnInit(): void {
    const data = this.modalService.state()?.data || {};
    this.load = data.load;
    const initialMode: LegModalMode = data.mode || 'split';
    this.mode.set(initialMode);
    if (Array.isArray(data.segments)) {
      this.segments.set(data.segments);
    }

    const editSeg: LoadSegment | null = data.segment || null;
    this.targetSegment.set(editSeg);

    this.form = this.fb.group({
      driverId: [null],
      driver2Id: [null],
      truckId: [null],
      trailerId: [null],
      customExchangeReason: [''],
      exchangeNotes: [''],
      companyName: [''],
      address: [null as Address | null],
    });

    if (initialMode === 'edit' && editSeg) {
      this.isTeamLeg.set(Boolean(editSeg.driver2Id));
      this.exchangeType.set(editSeg.exchangeType || 'full_swap');

      const existingReason = (editSeg.exchangeReason || '').trim();
      if ((PRESET_REASONS as readonly string[]).includes(existingReason)) {
        this.selectedReasonChip.set(existingReason);
      } else if (existingReason && existingReason !== 'Relay Handoff') {
        this.selectedReasonChip.set('Other');
        this.form.patchValue({ customExchangeReason: existingReason });
      } else {
        this.selectedReasonChip.set('Scheduled Relay');
      }

      const boundaryStop = this.existingSplitStop();
      if (boundaryStop) {
        this.servicedBy.set(
          boundaryStop.servicedBySegmentId === editSeg.id ? 'incoming' : 'outgoing'
        );
      }

      this.form.patchValue({
        driverId: editSeg.driverId || null,
        driver2Id: editSeg.driver2Id || null,
        truckId: editSeg.truckId || null,
        trailerId: editSeg.trailerId || null,
        exchangeNotes: editSeg.exchangeNotes || '',
      });
      this.selectedDriver1Id.set(editSeg.driverId || null);
      this.selectedDriver2Id.set(editSeg.driver2Id || null);
    } else {
      // Split mode defaults
      const requestedStopNum: number | undefined =
        data.initialStopNumber ?? data.defaultStopNumber;
      const defaultOpt = this.splitPointOptions()[0]?.value;
      const resolvedPt =
        requestedStopNum != null
          ? requestedStopNum
          : defaultOpt != null
            ? Number(defaultOpt)
            : -1;
      this.onSplitPointChange(String(resolvedPt));
      this.applySplitSwapPreset();
    }

    this.subs.add(
      this.form.get('driverId')!.valueChanges.subscribe((id) => {
        this.selectedDriver1Id.set(id || null);
        void this.refreshConflictChecks();
      })
    );
    this.subs.add(
      this.form.get('driver2Id')!.valueChanges.subscribe((id) => {
        this.selectedDriver2Id.set(id || null);
      })
    );
    this.subs.add(
      this.form.get('truckId')!.valueChanges.subscribe(() => void this.refreshConflictChecks())
    );

    if (this.segments().length === 0 && this.load?.id) {
      this.segmentService
        .getSegmentsForLoad(this.load.id)
        .then((segs) => {
          this.segments.set(segs);
          if (this.mode() === 'split') {
            this.applySplitSwapPreset();
          }
        })
        .catch(() => {});
    }

    this.yardService
      .initialize()
      .then(() => {
        const yards = this.yardService.allYards();
        if (yards.length > 0 && !this.selectedYardId()) {
          this.onYardSelect(yards[0].id);
        } else if (yards.length === 0) {
          this.locationType.set('custom');
        }
      })
      .catch(() => {
        this.locationType.set('custom');
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSplitPointChange(valStr: string): void {
    const pt = Number(valStr);
    if (isNaN(pt)) return;
    this.splitPoint.set(pt);
    if (pt > 0) {
      const stop = (this.load?.stops || []).find((s) => s.stopNumber === pt);
      if (stop) {
        this.servicedBy.set(stop.stopType === 'pickup' ? 'incoming' : 'outgoing');
      }
    }
    this.applySplitSwapPreset();
  }

  onExchangeTypeChange(type: string): void {
    this.exchangeType.set(type as ExchangeType);
    if (this.mode() === 'split') {
      this.applySplitSwapPreset();
    }
  }

  private applySplitSwapPreset(): void {
    if (!this.form || this.mode() !== 'split') return;
    const outSeg = this.outgoingSegment();
    const outDriver = outSeg?.driverId || this.load?.driver1Id || null;
    const outTruck = outSeg?.truckId || this.load?.truckId || null;
    const outTrailer = outSeg?.trailerId || this.load?.trailerId || null;

    const ex = this.exchangeType();
    if (ex === 'driver_only') {
      this.form.patchValue(
        {
          truckId: outTruck,
          trailerId: outTrailer,
        },
        { emitEvent: false }
      );
    } else if (ex === 'equipment_only') {
      this.form.patchValue(
        {
          driverId: outDriver,
        },
        { emitEvent: false }
      );
      this.selectedDriver1Id.set(outDriver);
    } else {
      if (!this.form.value.trailerId && outTrailer) {
        this.form.patchValue({ trailerId: outTrailer }, { emitEvent: false });
      }
    }
  }

  onToggleTeam(enabled: boolean): void {
    this.isTeamLeg.set(enabled);
    if (!enabled) {
      this.form.patchValue({ driver2Id: null });
    }
  }

  onLocationTypeChange(type: 'saved' | 'custom'): void {
    this.locationType.set(type);
    if (type === 'saved') {
      const yards = this.yardService.allYards();
      const id = this.selectedYardId() || (yards.length > 0 ? yards[0].id : null);
      if (id) this.onYardSelect(id);
    } else {
      this.selectedYardId.set(null);
      this.form.patchValue({ companyName: '', address: null });
    }
  }

  onYardSelect(yardId: string): void {
    this.selectedYardId.set(yardId);
    const yard = this.yardService.allYards().find((y) => y.id === yardId);
    if (yard) {
      this.form.patchValue({
        companyName: yard.name,
        address: yard.address,
      });
    }
  }

  onSelectReasonChip(chipValue: string | number): void {
    this.selectedReasonChip.set(String(chipValue));
  }

  private resolveExchangeReason(): string {
    const chip = this.selectedReasonChip();
    if (chip === 'Other') {
      return this.form.value.customExchangeReason?.trim() || 'Other';
    }
    return chip || 'Scheduled Relay';
  }

  formatCityStateZip(address?: Address | null): string {
    if (!address) return '';
    const cityState = [address.city, address.state].filter(Boolean).join(', ');
    return [cityState, address.zip].filter(Boolean).join(' ');
  }

  private async refreshConflictChecks(): Promise<void> {
    if (!this.load) return;
    const val = this.form.getRawValue();
    const stops = this.load.stops || [];
    const startStr = stops[0]?.appointment?.from?.date;
    const endStr = stops[stops.length - 1]?.appointment?.from?.date;
    const startDate = startStr ? new Date(startStr) : new Date();
    const endDate = endStr
      ? new Date(endStr)
      : new Date(startDate.getTime() + 5 * 24 * 60 * 60 * 1000);

    try {
      if (val.driverId) {
        const res = await this.segmentService.checkDriverAvailability(
          val.driverId,
          startDate,
          endDate,
          this.load.id
        );
        this.driverConflicts.set(res.conflicts);
      } else {
        this.driverConflicts.set([]);
      }

      if (val.truckId) {
        const res = await this.segmentService.checkEquipmentAvailability(
          val.truckId,
          'truck',
          startDate,
          endDate,
          this.load.id
        );
        this.truckConflicts.set(res.conflicts);
      } else {
        this.truckConflicts.set([]);
      }
    } catch {
      // Non-blocking
    }
  }

  async save(): Promise<void> {
    const val = this.form.getRawValue();
    const resolvedReason = this.resolveExchangeReason();

    if (
      (this.mode() === 'split' || (this.targetSegment()?.segmentNumber || 1) > 1) &&
      this.selectedReasonChip() === 'Other' &&
      !val.customExchangeReason?.trim()
    ) {
      this.toastService.show('warning', 'Please enter a reason when "Other" is selected.', {
        title: 'Reason Required',
      });
      return;
    }

    if (this.mode() === 'edit') {
      const target = this.targetSegment();
      if (!target) return;

      this.isSaving.set(true);
      try {
        // Note: pay override & deadhead fields are intentionally omitted (undefined)
        // so editing operational leg assignments never overwrites payroll adjustments.
        await this.segmentService.updateLegAssignment(
          this.load,
          target.id,
          {
            driverId: val.driverId || '',
            driver2Id: this.isTeamLeg() ? val.driver2Id || null : null,
            truckId: val.truckId || '',
            trailerId: val.trailerId || '',
            exchangeType: target.segmentNumber > 1 ? this.exchangeType() : target.exchangeType,
            exchangeReason:
              target.segmentNumber > 1 ? resolvedReason : target.exchangeReason,
            exchangeNotes:
              target.segmentNumber > 1 ? val.exchangeNotes?.trim() || null : target.exchangeNotes,
            fromStopServicedBy:
              target.segmentNumber > 1 && this.showCommercialBoundaryDuty()
                ? this.servicedBy()
                : undefined,
          },
          this.segments()
        );

        this.toastService.show('success', `Leg ${target.segmentNumber} updated successfully.`, {
          title: 'Leg Updated',
        });
        this.modalService.state()?.onSuccess?.();
        this.modalService.close();
      } catch (err: any) {
        console.error('Failed to update leg:', err);
        this.toastService.show('error', err?.message || 'Failed to update leg.', {
          title: 'Update Failed',
        });
      } finally {
        this.isSaving.set(false);
      }
      return;
    }

    // Mode === 'split'
    const outSeg = this.outgoingSegment();
    const ex = this.exchangeType();
    const effectiveDriverId =
      ex === 'equipment_only' ? outSeg?.driverId || this.load.driver1Id || '' : val.driverId || '';
    const effectiveTruckId =
      ex === 'driver_only' ? outSeg?.truckId || this.load.truckId || '' : val.truckId || '';
    const effectiveTrailerId =
      ex === 'driver_only'
        ? outSeg?.trailerId || this.load.trailerId || ''
        : val.trailerId || outSeg?.trailerId || this.load.trailerId || '';

    if (ex !== 'equipment_only' && !effectiveDriverId) {
      this.toastService.show('warning', 'Please select the oncoming driver for the new leg.', {
        title: 'Driver Required',
      });
      return;
    }
    if (ex !== 'driver_only' && !effectiveTruckId) {
      this.toastService.show('warning', 'Please select the oncoming truck for the new leg.', {
        title: 'Truck Required',
      });
      return;
    }

    const pt = this.splitPoint();
    let relayCompanyName = '';
    let relayAddress: Address | null = null;

    if (pt < 0) {
      if (this.locationType() === 'saved' && this.hasSavedYards()) {
        const yard = this.selectedYard();
        if (!yard) {
          this.toastService.show('warning', 'Please select a relay yard or terminal.', {
            title: 'Relay Location Required',
          });
          return;
        }
        relayCompanyName = yard.name;
        relayAddress = { ...yard.address };
      } else {
        relayCompanyName = val.companyName?.trim() || '';
        relayAddress = val.address ? { ...val.address } : null;
        if (!relayCompanyName || !relayAddress || (!relayAddress.city && !relayAddress.street)) {
          this.toastService.show(
            'warning',
            'Please provide a facility/yard name and address for the relay stop.',
            { title: 'Relay Location Required' }
          );
          return;
        }
      }
    }

    this.isSaving.set(true);
    try {
      const oncomingLeg = {
        driverId: effectiveDriverId,
        driver2Id: this.isTeamLeg() ? val.driver2Id || undefined : undefined,
        truckId: effectiveTruckId,
        trailerId: effectiveTrailerId,
        exchangeType: ex,
        exchangeReason: resolvedReason,
        exchangeNotes: val.exchangeNotes?.trim() || null,
        exchangeLocation: relayAddress || this.existingSplitStop()?.address || null,
      };

      if (pt < 0) {
        if (relayAddress && (!relayAddress.lat || !relayAddress.lng)) {
          try {
            const coords = await this.googleMapsService.geocodeAddress(relayAddress);
            if (coords) {
              relayAddress.lat = coords.lat;
              relayAddress.lng = coords.lng;
            }
          } catch {
            // Non-blocking
          }
        }
        const afterStopNumber = Math.abs(pt);
        const relayStop: Stop = {
          stopType: 'handoff',
          createdForRelay: true,
          companyName: relayCompanyName,
          address: relayAddress,
          handoffReason: oncomingLeg.exchangeReason,
          instructions: oncomingLeg.exchangeNotes || null,
          requiresApproval: false,
          status: 'PENDING',
        };
        await this.segmentService.insertStopAndReconcile(
          this.load,
          {
            newStop: relayStop,
            afterStopNumber,
            split: {
              oncomingLeg,
              servicedBy: 'none',
            },
          },
          this.segments()
        );
      } else {
        await this.segmentService.splitLegAtStop(
          this.load,
          {
            stopNumber: pt,
            oncomingLeg,
            servicedBy: this.showCommercialBoundaryDuty() ? this.servicedBy() : 'none',
          },
          this.segments()
        );
      }

      this.toastService.show(
        'success',
        pt < 0
          ? 'Relay stop inserted and oncoming leg scheduled.'
          : `Route split at Stop #${pt} and oncoming leg scheduled.`,
        { title: 'Route Split Scheduled' }
      );
      this.modalService.state()?.onSuccess?.();
      this.modalService.close();
    } catch (err: any) {
      console.error('Failed to split route:', err);
      this.toastService.show('error', err?.message || 'Failed to split route.', {
        title: 'Split Failed',
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
